import { useState } from "react";
import { toast } from "sonner";
import { BRAND, WORKSHOPS } from "@/content";
import { submitWorkshop } from "@/api";

const initial = {
  name: "", email: "", organization: "", phone: "",
  workshop_type: "women", preferred_date: "", participants: "", message: "",
};

export default function Workshops() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.workshop_type) {
      toast.error("Please complete name, email, and choose a workshop type.");
      return;
    }
    setSending(true);
    try {
      await submitWorkshop(form);
      setDone(true); setForm(initial);
      toast.success("Workshop request sent.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="page-workshops" className="bg-white">
      <section className="pt-[120px] pb-16">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="overline">Workshops</p>
            <h1 className="display mt-6 text-[44px] md:text-[120px]">
              Half a day.<br/>A different <em className="font-serif text-[var(--ic-accent)]">life</em>.
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <p className="font-sans text-[17px] leading-[1.75] text-black/75">
              Designed for women's gatherings, schools, wellness retreats, and community
              organizations who want their people to leave changed — not just informed.
            </p>
          </div>
        </div>
      </section>

      <section className="hairline-b">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
          <div className="zoom-img-wrap relative overflow-hidden border border-[var(--ic-line)] aspect-[16/10] bg-[var(--ic-warm)]">
            <img src={BRAND.images.workshop} alt="Crystal Purdue leading a high school workshop" className="zoom-img absolute inset-0 w-full h-full object-cover object-[center_25%]" />
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="overline">Choose your audience</p>
          <h2 className="section-display mt-4 mb-12">Four formats. One promise.</h2>

          <div className="grid grid-cols-12 gap-6 lg:gap-8">
            {WORKSHOPS.map((w, i) => (
              <article key={w.key} data-testid={`workshop-${w.key}`}
                className="col-span-12 sm:col-span-6 lg:col-span-3 border border-[var(--ic-line)] p-8 bg-white hover:bg-[var(--ic-warm)] transition-colors">
                <p className="overline">0{i+1}</p>
                <h3 className="font-serif text-[28px] mt-6 leading-tight">{w.title}</h3>
                <p className="text-[14.5px] text-black/70 mt-3 leading-relaxed">{w.blurb}</p>
                <button onClick={() => setForm((f) => ({ ...f, workshop_type: w.key }))}
                  data-testid={`workshop-select-${w.key}`}
                  className="ed-link mt-6 inline-block text-[11px]">
                  Choose this format
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="request" className="bg-[var(--ic-warm)] py-24 lg:py-32 border-y border-[var(--ic-line)]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline">Workshop request</p>
            <h2 className="section-display mt-4">Request your<br/>workshop.</h2>
            <p className="font-sans text-[15px] text-black/65 mt-6 max-w-sm">
              We'll get back to you within two business days with available dates, group sizes,
              and pricing tiers.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-8">
            {done ? (
              <div className="border border-[var(--ic-line)] bg-white p-10" data-testid="workshop-success">
                <p className="overline">Request received</p>
                <h3 className="font-serif text-[34px] mt-4">Thank you.</h3>
                <p className="font-sans text-[16px] text-black/70 mt-4 max-w-xl">
                  Your workshop request is in. We'll follow up within two business days.
                </p>
                <button className="ed-link mt-8" onClick={() => setDone(false)}>Submit another request</button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-12 gap-6 bg-white p-8 border border-[var(--ic-line)]" data-testid="workshop-form">
                <FieldText col={6} label="Name *"        v={form.name} on={onChange("name")} testid="wk-name"/>
                <FieldText col={6} label="Email *"       v={form.email} on={onChange("email")} type="email" testid="wk-email"/>
                <FieldText col={6} label="Organization"  v={form.organization} on={onChange("organization")} testid="wk-org"/>
                <FieldText col={6} label="Phone"         v={form.phone} on={onChange("phone")} testid="wk-phone"/>
                <div className="col-span-12 md:col-span-6">
                  <label className="ed-label block">Workshop type *</label>
                  <select className="ed-select" data-testid="wk-type" value={form.workshop_type} onChange={onChange("workshop_type")}>
                    {WORKSHOPS.map((w) => (<option key={w.key} value={w.key}>{w.title}</option>))}
                  </select>
                </div>
                <FieldText col={6} label="Preferred date"  v={form.preferred_date} on={onChange("preferred_date")} testid="wk-date"/>
                <FieldText col={6} label="Participants"    v={form.participants}   on={onChange("participants")}   testid="wk-participants"/>
                <FieldArea col={12} label="Anything else we should know?" v={form.message} on={onChange("message")} testid="wk-message"/>
                <div className="col-span-12">
                  <button type="submit" disabled={sending} data-testid="wk-submit" className="btn-ink disabled:opacity-50">
                    {sending ? "Sending…" : "Send Workshop Request"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function FieldText({ col=12, label, v, on, type="text", testid }) {
  return (
    <div className={`col-span-12 md:col-span-${col}`}>
      <label className="ed-label block">{label}</label>
      <input data-testid={testid} type={type} value={v} onChange={on} className="ed-input" />
    </div>
  );
}
function FieldArea({ col=12, label, v, on, testid }) {
  return (
    <div className={`col-span-12 md:col-span-${col}`}>
      <label className="ed-label block">{label}</label>
      <textarea data-testid={testid} value={v} onChange={on} className="ed-textarea" rows={5} />
    </div>
  );
}
